import { STRIPE_CONFIG } from '@/constants/stripe';
import {
  type StripeCardElement,
  type StripeCardNumberElement,
  type Stripe,
  type PaymentMethod,
  type StripeElements,
  type CreatePaymentMethodData,
  loadStripe,
} from '@stripe/stripe-js';

export interface BillingDetails {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}

class StripeServiceClass {
  private stripe: Stripe | null = null;
  private stripeExpress: Stripe | null = null;
  initialize(stripe: Stripe) {
    this.stripe = stripe;
  }

  initilizeStripeExpress(stripe: Stripe) {
    this.stripeExpress = stripe;
  }

  getStripeExpress(): Stripe {
    if (!this.stripeExpress) {
      throw new Error('Stripe Express is not initialized');
    }
    return this.stripeExpress;
  }
  async createStripeInstance(paymentKey: string): Promise<Stripe> {
    const stripe = await loadStripe(paymentKey, STRIPE_CONFIG);
    if (!stripe) {
      throw new Error('Failed to create stripe instance');
    }
    return stripe;
  }

  getStripe(): Stripe {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }
    return this.stripe;
  }

  updateStripeInstance(stripe: Stripe) {
    this.stripe = stripe;
  }
  async createGenericBankRequiredPaymentMethod(
    params: CreatePaymentMethodData & { elements: StripeElements }
  ): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }
    if (!params.elements) {
      throw new Error('Elements are required');
    }
    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: params.elements,
      params: {
        billing_details: params.billing_details,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createGenegicStripePaymentMethod(params: CreatePaymentMethodData): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      type: params.type,
      ...params,
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async getUrlFromSecretClientIntent(clientSecret: string): Promise<string | undefined | null> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const response = await this.stripe.retrievePaymentIntent(clientSecret);
    return response.paymentIntent?.next_action?.redirect_to_url?.url;
  }

  async createGooglePayPaymentMethod(
    elements: StripeElements,
    billingDetails?: BillingDetails
  ): Promise<PaymentMethod> {
    if (!this.stripeExpress) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripeExpress.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createGenericPaymentMethod(elements: StripeElements, billingDetails?: BillingDetails): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }
  async createApplePayPaymentMethod(elements: StripeElements, billingDetails?: BillingDetails): Promise<PaymentMethod> {
    if (!this.stripeExpress) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripeExpress.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createLinkPaymentMethod(elements: StripeElements, billingDetails?: unknown): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements,
      params: billingDetails as Record<string, unknown>,
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createAmazonPayPaymentMethod(
    elements: StripeElements,
    billingDetails?: BillingDetails
  ): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createKlarnaPaymentMethod(elements: StripeElements, billingDetails?: BillingDetails): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createIdealPaymentMethod(elements: StripeElements, billingDetails?: BillingDetails): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createPaymentMethod(
    cardElement: StripeCardElement | StripeCardNumberElement,
    billingDetails?: BillingDetails
  ): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: billingDetails,
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }
}

export const stripeService = new StripeServiceClass();
